import { emailsGraphic, pdfsGraphic } from '@sparklayer-io/spark-dash-ui/assets';
import { MediaCard, Page } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { featureRestricted, features, getActiveBillingPlanKey } from '../../../constants/features';
import { useEnv } from '../../../context/env-context';
import { withPageProviders } from '../../../context/page-context';
import { ThemesEmptyState } from '../components/themes-empty-state';
import { ThemesTable } from '../components/themes-table';
import { useThemes } from '../hooks/use-themes';
import type { Theme } from '../types/theme';
import { DeleteThemeModal } from './delete-theme-modal';
import { ThemeNameModal, type ThemeNameModalState } from './theme-name-modal';
import { UploadThemeModal } from './upload-theme-modal';

const THEMES_LEARN_MORE_URL = 'https://docs.sparklayer.io/dashboard-configurations#j6s7E';

export const ThemesPage = withPageProviders(ThemesPageContent);

function ThemesPageContent() {
  const env = useEnv();
  const planHasThemes = !featureRestricted(
    features.THEMES,
    getActiveBillingPlanKey(env.billing.plan_key),
  );

  const { data: themes = [], isLoading, isError, refetch } = useThemes();

  const [nameModal, setNameModal] = useState<ThemeNameModalState>(null);
  const [uploadTheme, setUploadTheme] = useState<Theme | null>(null);
  const [deleteTheme, setDeleteTheme] = useState<Theme | null>(null);

  return (
    <Page
      title={t('features:themes.pages.themes_list_page.title')}
      description={t('features:themes.pages.themes_list_page.description')}
      learnMoreUrl={THEMES_LEARN_MORE_URL}
      footerDescription={t('features:themes.pages.themes_list_page.footer')}
      primaryAction={
        planHasThemes && themes.length > 0
          ? {
              label: t('features:themes.pages.themes_list_page.create_button'),
              icon: 'plusCircle',
              onClick: () => setNameModal({ mode: 'create' }),
            }
          : undefined
      }
    >
      <SparkSuspense
        isLoading={isLoading}
        isError={isError}
        errorMessage={t('errors:generic')}
        onReload={() => void refetch()}
      >
        {themes.length > 0 ? (
          <div className="flex flex-col gap-8">
            <ThemesTable
              themes={themes}
              onRename={(theme) => setNameModal({ mode: 'rename', theme })}
              onUpload={setUploadTheme}
              onDelete={setDeleteTheme}
            />
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <MediaCard
                headingLevel="h3"
                title={t('features:themes.pages.themes_list_page.media.pdfs.title')}
                description={t('features:themes.pages.themes_list_page.media.pdfs.description')}
                imgSrc={pdfsGraphic}
                action={{
                  content: t('links:learn_more'),
                  onClick: () =>
                    window.open(THEMES_LEARN_MORE_URL, '_blank', 'noopener,noreferrer'),
                }}
              />
              <MediaCard
                headingLevel="h3"
                title={t('features:themes.pages.themes_list_page.media.emails.title')}
                description={t('features:themes.pages.themes_list_page.media.emails.description')}
                imgSrc={emailsGraphic}
                action={{
                  content: t('links:learn_more'),
                  onClick: () =>
                    window.open(THEMES_LEARN_MORE_URL, '_blank', 'noopener,noreferrer'),
                }}
              />
            </div>
          </div>
        ) : (
          <ThemesEmptyState
            planHasThemes={planHasThemes}
            onCreate={() => setNameModal({ mode: 'create' })}
          />
        )}
      </SparkSuspense>

      <ThemeNameModal state={nameModal} onClose={() => setNameModal(null)} />
      <UploadThemeModal theme={uploadTheme} onClose={() => setUploadTheme(null)} />
      <DeleteThemeModal theme={deleteTheme} onClose={() => setDeleteTheme(null)} />
    </Page>
  );
}
